<template>
  <v-dialog
    v-model="dialog"
    max-width="800px"
    persistent
    :fullscreen="$vuetify.display.smAndDown"
    scrollable
    :retain-focus="false"
  >
    <v-card>
      <v-card-title class="redColor videos-title d-flex align-center ga-2">
        <span class="text-subtitle-1 font-weight-medium text-white flex-grow-1 videos-title-text">
          {{ $t("vehicle_entry.videos") }}
        </span>
        <v-btn
          variant="outlined"
          icon
          color="white"
          class="videos-close-btn flex-shrink-0"
          size="small"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row class="align-center justify-center text-center flex-wrap">
          <!----------------------------------------SWITCH----------------------------------------->
          <v-col cols="12" class="d-flex justify-center">
            <v-switch
              v-model="cameraSwitch"
              :label="$t('videos.camera')"
              color="primary"
            />
          </v-col>
          <!----------------------------------------UPLOAD----------------------------------------->
          <v-col v-if="!cameraSwitch && !video" cols="12">
            <v-btn
              color="primary"
              variant="outlined"
              @click="openInputComponent"
            >
              <v-icon start> mdi-paperclip </v-icon>
              {{ $t("videos.upload") }}
            </v-btn>

            <input
              ref="input"
              type="file"
              style="display: none"
              accept="video/mp4"
              @change="onInput"
            />
          </v-col>
          <!----------------------------------------CAMERA----------------------------------------->
          <v-col cols="12" :class="{ 'd-none': video || !cameraSwitch }">
            <video-recorder
              v-if="dialog && typeof video !== 'string'"
              ref="videoRecorder"
              @video="saveVideo"
            />
          </v-col>
          <!-----------------------------------------VIDEO----------------------------------------->
          <v-col cols="12" :class="{ 'd-none': !video }">
            <v-card flat>
              <v-row align="center" justify="center">
                <video
                  :src="videoSrc"
                  controls
                  playsinline
                  class="videos-preview-player"
                />
              </v-row>
              <v-card-actions>
                <v-spacer />
                <v-btn color="red-darken-1" @click="deleteVideo">
                  {{ $t("delete") }}
                  <v-icon end> mdi-delete </v-icon>
                </v-btn>
                <v-spacer />
              </v-card-actions>
            </v-card>
          </v-col>
          <!--------------------------------------------------------------------------------------->
        </v-row>
      </v-card-text>
      <v-card-actions class="videos-actions pa-3">
        <v-spacer class="d-none d-md-flex" />
        <v-btn class="videos-action-btn" color="error" variant="outlined" @click="close">
          {{ $t("vehicle_entry.close") }}
        </v-btn>
        <v-btn class="videos-action-btn" color="primary" variant="outlined" @click="save">
          {{ $t("vehicle_entry.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Video from "../video.vue";

function dataURLtoBlob(dataUrl) {
  // convert base64 to raw binary data held in a string
  const byteString = atob(dataUrl.split(",")[1]);
  // separate out the mime component
  const mimeString = dataUrl.split(",")[0].split(":")[1].split(";")[0];
  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);
  // create a view into the buffer
  const ia = new Uint8Array(ab);
  // set the bytes of the buffer to the correct values
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  // write the ArrayBuffer to a blob, and you're done
  return new Blob([ab], { type: mimeString });
}

export default {
  components: {
    "video-recorder": Video,
  },
  data: () => ({
    dialog: false,
    cameraSwitch: true,
    video: null,
    videoSrc: null,
  }),
  watch: {},
  methods: {
    openInputComponent() {
      this.$refs.input.click();
    },
    onInput(input) {
      const { files } = input.target;
      for (const file of files) {
        // eslint-disable-line no-restricted-syntax
        if (file && file !== undefined) {
          const fileReader = new FileReader();
          fileReader.readAsDataURL(file);
          fileReader.addEventListener("load", () => {
            const dataUrl = fileReader.result;
            const blob = dataURLtoBlob(dataUrl);
            this.video = blob;
            this.videoSrc = URL.createObjectURL(blob);
            this.stopCamera();
          });
        }
      }
      input.target.value = "";
    },
    open(video) {
      this.dialog = true;
      if (typeof video === "string" && video) {
        this.video = video;
        this.videoSrc = `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${video}`;
      } else if (video && video.blob) {
        this.video = video.blob;
        this.videoSrc = URL.createObjectURL(video.blob);
      }
    },
    save() {
      if (typeof this.video === "string") {
        this.$emit("save", { video: this.video });
        this.close();
        return;
      }
      const random = Math.random().toString(36).substring(9);
      const timestamp = new Date().getTime().toString(36);

      const video = this.video
        ? {
            title: `${random}${timestamp}.mp4`,
            blob: this.video,
            dataUrl: this.videoSrc,
          }
        : null;
      this.$emit("save", { video });
      this.close();
    },
    close() {
      this.dialog = false;
      this.cameraSwitch = true;
      this.video = null;
      this.videoSrc = null;
      this.$emit("close");
    },
    saveVideo(data) {
      this.video = data.blob;
      this.videoSrc = URL.createObjectURL(data.blob);
      this.stopCamera();
    },
    deleteVideo() {
      this.video = null;
      this.videoSrc = null;
      this.startCamera();
    },

    startCamera() {
      const { videoRecorder } = this.$refs;
      if (videoRecorder) videoRecorder.startCamera();
    },
    stopCamera() {
      const { videoRecorder } = this.$refs;
      if (videoRecorder) videoRecorder.stopCamera();
    },

    hide() {
      this.dialog = false;
    },
    show() {
      this.dialog = true;
    },
  },
};
</script>

<style scoped>
.videos-title {
  padding: 12px 16px;
}

.videos-title-text {
  min-width: 0;
  line-height: 1.35;
  word-break: break-word;
}

.videos-preview-player {
  display: block;
  width: 100%;
  max-width: 680px;
  height: auto;
  max-height: calc(100vh - 280px);
}

@media (max-width: 959.98px) {
  .videos-title {
    position: relative;
    padding: 12px 52px 12px 16px;
  }

  .videos-close-btn {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
  }

  .videos-preview-player {
    max-height: calc(100dvh - 280px);
  }

  .videos-actions {
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }

  .videos-actions .v-btn {
    flex: 1 1 calc(50% - 8px);
    min-width: 0;
  }

  /* If the dialog is narrow (some phones), make actions stack nicely */
  .videos-action-btn {
    width: 100%;
  }
}
</style>
